import type { FormField } from './form-types';
import type { ShaderMetadata } from './shader-types';

/**
 * Abstract base class for all shader models.
 * Provides common properties and methods that all shaders must implement.
 */
export abstract class BaseShader {
  // Common properties that all shaders have
  abstract name: string;
  abstract width: number;
  abstract height: number;
  abstract referenceWidth: number;
  abstract referenceHeight: number;
  abstract rotation: number;

  /**
   * Constructor for the base shader.
   * Subclasses should handle their own property initialization.
   */
  constructor() {
    // Base constructor does nothing - subclasses handle initialization
  }

  /**
   * Generate the GLSL fragment shader code for this shader.
   * Must be implemented by each shader class.
   */
  abstract generate(): string;

  /**
   * Get the form configuration for this shader's parameters.
   * Must be implemented by each shader class as a static method.
   */
  static getFormConfig(): FormField[] {
    throw new Error('getFormConfig must be implemented by subclass');
  }

  /**
   * Get metadata about this shader's behavior and categorization.
   * Must be implemented by each shader class as a static method.
   */
  static getMetadata(): ShaderMetadata {
    throw new Error('getMetadata must be implemented by subclass');
  }
}

/**
 * Type helper to ensure shader constructors have the required static methods
 */
export interface ShaderConstructor<T extends BaseShader = BaseShader> {
  new (props?: Partial<T>): T;
  getFormConfig(): FormField[];
  getMetadata(): ShaderMetadata;
}
